/**
 * La photo d'un ticket, préparée pour la lecture dans le téléphone.
 *
 * <p>Le ticket posé sur une table à motifs (bois, nappe, set doré) se lisait
 * mal : le décor autour devenait des signes que la lecture prenait pour du
 * texte, et le ticket du Relais Tamoil donnait « CN PEN » et 3 francs au lieu
 * de « RELAIS TAMOIL » et 3.40. Le ticket est découpé dans la photo, puis
 * passé en noir et blanc net, ombre de la main comprise.
 *
 * <p>Les fonctions travaillent sur des pixels ({data, width, height}, comme
 * une ImageData), sans toile : elles s'éprouvent hors du navigateur.
 */

/** Côté de la grille qui cherche le papier : assez fin pour un ticket étroit. */
const GRILLE = 300;

/** Un ticket plus petit que cette part de la photo n'est pas cherché. */
const PART_MIN = 0.12;

/**
 * Le cadre du ticket dans la photo : la plus grande tache claire et sans
 * couleur, le papier.
 *
 * @param {{data: Uint8ClampedArray|Uint8Array, width: number, height: number}} img pixels RVBA
 * @returns {{x: number, y: number, w: number, h: number}} le cadre, ou toute la
 *   photo quand aucun papier n'y est trouvé
 */
export function cadreDuTicket({ data, width, height }) {
  const toute = { x: 0, y: 0, w: width, h: height };
  const pas = Math.max(1, Math.round(Math.max(width, height) / GRILLE));
  const gw = Math.floor(width / pas);
  const gh = Math.floor(height / pas);
  if (gw < 4 || gh < 4) return toute;
  const papier = new Uint8Array(gw * gh);
  for (let gy = 0; gy < gh; gy += 1) {
    for (let gx = 0; gx < gw; gx += 1) {
      let lum = 0;
      let sat = 0;
      let n = 0;
      for (let y = gy * pas; y < (gy + 1) * pas; y += 2) {
        for (let x = gx * pas; x < (gx + 1) * pas; x += 2) {
          const i = (y * width + x) * 4;
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          lum += 0.299 * r + 0.587 * g + 0.114 * b;
          sat += Math.max(r, g, b) - Math.min(r, g, b);
          n += 1;
        }
      }
      // Le papier est clair et gris ; le bois, le doré, la main sont colorés,
      // le fond sombre ne passe pas. Les lettres noires du ticket font des
      // trous dans la tache, sans changer son cadre.
      papier[gy * gw + gx] = lum / n > 105 && sat / n < 48 ? 1 : 0;
    }
  }
  // La plus grande tache de papier d'un seul tenant.
  const vue = new Int32Array(gw * gh).fill(-1);
  const pile = new Int32Array(gw * gh);
  let meilleur = null;
  for (let depart = 0; depart < papier.length; depart += 1) {
    if (!papier[depart] || vue[depart] !== -1) continue;
    let haut = 0;
    pile[haut++] = depart;
    vue[depart] = depart;
    let taille = 0;
    let x0 = gw;
    let y0 = gh;
    let x1 = 0;
    let y1 = 0;
    while (haut) {
      const c = pile[--haut];
      const cx = c % gw;
      const cy = (c - cx) / gw;
      taille += 1;
      if (cx < x0) x0 = cx;
      if (cx > x1) x1 = cx;
      if (cy < y0) y0 = cy;
      if (cy > y1) y1 = cy;
      const voisins = [
        cx > 0 ? c - 1 : -1,
        cx < gw - 1 ? c + 1 : -1,
        cy > 0 ? c - gw : -1,
        cy < gh - 1 ? c + gw : -1,
      ];
      for (const v of voisins) {
        if (v >= 0 && papier[v] && vue[v] === -1) {
          vue[v] = depart;
          pile[haut++] = v;
        }
      }
    }
    if (!meilleur || taille > meilleur.taille) meilleur = { taille, x0, y0, x1, y1, id: depart };
  }
  if (!meilleur) return toute;
  const largeur = meilleur.x1 - meilleur.x0 + 1;
  const hauteur = meilleur.y1 - meilleur.y0 + 1;
  if (largeur * hauteur < gw * gh * PART_MIN) return toute;
  // Le contour du papier : ses bords, rang par rang, enveloppés. Un ticket
  // penché n'est pas un rectangle, et le décor resté dans le cadre, sur ses
  // bords, se lisait comme du texte. L'enveloppe passe par-dessus les lettres.
  const bords = [];
  for (let gy = meilleur.y0; gy <= meilleur.y1; gy += 1) {
    let a = -1;
    let b = -1;
    for (let gx = meilleur.x0; gx <= meilleur.x1; gx += 1) {
      if (vue[gy * gw + gx] !== meilleur.id) continue;
      if (a < 0) a = gx;
      b = gx;
    }
    if (a < 0) continue;
    bords.push([a * pas, gy * pas], [(b + 1) * pas, gy * pas]);
    bords.push([a * pas, (gy + 1) * pas], [(b + 1) * pas, (gy + 1) * pas]);
  }
  // Une marge d'une case autour : le bord du papier reste dans le cadre.
  const x = Math.max(0, (meilleur.x0 - 1) * pas);
  const y = Math.max(0, (meilleur.y0 - 1) * pas);
  return {
    x,
    y,
    w: Math.min(width, (meilleur.x1 + 2) * pas) - x,
    h: Math.min(height, (meilleur.y1 + 2) * pas) - y,
    contour: enveloppe(bords),
  };
}

/**
 * L'enveloppe convexe de points.
 *
 * @param {number[][]} points [x, y]
 * @returns {number[][]} les sommets, dans l'ordre
 */
function enveloppe(points) {
  const tries = [...points].sort((p, q) => p[0] - q[0] || p[1] - q[1]);
  if (tries.length < 3) return tries;
  const tour = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const moitie = (liste) => {
    const h = [];
    for (const p of liste) {
      while (h.length >= 2 && tour(h[h.length - 2], h[h.length - 1], p) <= 0) h.pop();
      h.push(p);
    }
    h.pop();
    return h;
  };
  return [...moitie(tries), ...moitie([...tries].reverse())];
}

/**
 * Blanchit, dans l'image découpée au cadre, tout ce qui sort du contour du papier.
 *
 * @param {{data: Uint8ClampedArray|Uint8Array, width: number, height: number}} img image
 *   découpée au cadre, éventuellement agrandie ; modifiée sur place
 * @param {{x: number, y: number, w: number, contour?: number[][]}} cadre cadre du ticket
 * @returns {object} l'image reçue
 */
export function blanchitAutour(img, cadre) {
  const contour = cadre && cadre.contour;
  if (!contour || contour.length < 3) return img;
  const { data, width, height } = img;
  const f = width / cadre.w;
  for (let y = 0; y < height; y += 1) {
    // La largeur du contour à cette hauteur de la photo.
    const yp = cadre.y + (y + 0.5) / f;
    let debut = Infinity;
    let fin = -Infinity;
    for (let k = 0; k < contour.length; k += 1) {
      const [xa, ya] = contour[k];
      const [xb, yb] = contour[(k + 1) % contour.length];
      if ((yp < ya && yp < yb) || (yp > ya && yp > yb)) continue;
      const x = ya === yb ? Math.min(xa, xb) : xa + ((yp - ya) * (xb - xa)) / (yb - ya);
      const x2 = ya === yb ? Math.max(xa, xb) : x;
      debut = Math.min(debut, x);
      fin = Math.max(fin, x2);
    }
    const a = (debut - cadre.x) * f;
    const b = (fin - cadre.x) * f;
    for (let x = 0; x < width; x += 1) {
      if (x >= a && x < b) continue;
      const i = (y * width + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = 255;
    }
  }
  return img;
}

/**
 * Passe la photo en noir et blanc net, chaque point comparé à la moyenne de
 * ses voisins : l'ombre de la main ou du téléphone sur une moitié du ticket
 * n'efface plus les chiffres.
 *
 * @param {{data: Uint8ClampedArray|Uint8Array, width: number, height: number}} img pixels
 *   RVBA, modifiés sur place
 * @returns {object} l'image reçue, en noir et blanc
 */
export function noirEtBlanc(img, seuil = 0.85) {
  const { data, width, height } = img;
  // Entiers : un téléphone garde peu de mémoire pour une photo agrandie.
  const gris = new Uint8Array(width * height);
  for (let p = 0, i = 0; p < gris.length; p += 1, i += 4) {
    gris[p] = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
  }
  // Sommes cumulées : la moyenne d'un carré se lit en quatre valeurs.
  const somme = new Uint32Array((width + 1) * (height + 1));
  for (let y = 0; y < height; y += 1) {
    let ligne = 0;
    for (let x = 0; x < width; x += 1) {
      ligne += gris[y * width + x];
      somme[(y + 1) * (width + 1) + x + 1] = somme[y * (width + 1) + x + 1] + ligne;
    }
  }
  const demi = Math.max(8, Math.round(width / 24));
  for (let y = 0; y < height; y += 1) {
    const ya = Math.max(0, y - demi);
    const yb = Math.min(height, y + demi + 1);
    for (let x = 0; x < width; x += 1) {
      const xa = Math.max(0, x - demi);
      const xb = Math.min(width, x + demi + 1);
      const n = (xb - xa) * (yb - ya);
      const s =
        somme[yb * (width + 1) + xb] -
        somme[ya * (width + 1) + xb] -
        somme[yb * (width + 1) + xa] +
        somme[ya * (width + 1) + xa];
      const v = gris[y * width + x] * n <= s * seuil ? 0 : 255;
      const i = (y * width + x) * 4;
      data[i] = v;
      data[i + 1] = v;
      data[i + 2] = v;
      data[i + 3] = 255;
    }
  }
  return img;
}

/**
 * Efface les taches noires qui ne sont pas des lettres : le décor resté au
 * bord du ticket, les filets du tableau, le bord d'ombre du papier. Collées
 * aux montants, elles les faisaient sauter (« 3.40 », « 0.25 » au bord droit).
 *
 * @param {{data: Uint8ClampedArray|Uint8Array, width: number, height: number}} img image
 *   en noir et blanc ({@link noirEtBlanc}), modifiée sur place
 * @returns {object} l'image reçue
 */
export function sansGrossesTaches(img) {
  const { data, width, height } = img;
  const hautMax = Math.max(12, height * 0.07);
  const largeMax = Math.max(12, width * 0.3);
  const noir = (p) => data[p * 4] === 0;
  const vu = new Uint8Array(width * height);
  const pile = new Int32Array(width * height);
  // Parcourt la tache de « depart » ; `garde` dit quels points la suivent.
  const parcours = (depart, garde, chaque) => {
    let haut = 0;
    pile[haut++] = depart;
    while (haut) {
      const p = pile[--haut];
      chaque(p);
      const x = p % width;
      if (x > 0 && garde(p - 1)) pile[haut++] = p - 1;
      if (x < width - 1 && garde(p + 1)) pile[haut++] = p + 1;
      if (p >= width && garde(p - width)) pile[haut++] = p - width;
      if (p < width * (height - 1) && garde(p + width)) pile[haut++] = p + width;
    }
  };
  for (let depart = 0; depart < vu.length; depart += 1) {
    if (vu[depart] || !noir(depart)) continue;
    let x0 = width;
    let y0 = height;
    let x1 = 0;
    let y1 = 0;
    vu[depart] = 1;
    parcours(
      depart,
      (v) => {
        if (vu[v] || !noir(v)) return false;
        vu[v] = 1;
        return true;
      },
      (p) => {
        const x = p % width;
        const y = (p - x) / width;
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      },
    );
    if (y1 - y0 + 1 <= hautMax && x1 - x0 + 1 <= largeMax) continue;
    // Trop grande pour une lettre : la tache passe en blanc, point par point.
    const blanchit = (p) => {
      data[p * 4] = 255;
      data[p * 4 + 1] = 255;
      data[p * 4 + 2] = 255;
    };
    blanchit(depart);
    parcours(
      depart,
      (v) => {
        if (!noir(v)) return false;
        blanchit(v);
        return true;
      },
      () => {},
    );
  }
  return img;
}
